import { createTheme } from '@mui/material/styles';
import type { Palette } from '@mui/material/styles';

import { important } from '../../utils/css';
import { createSpacing } from './constants';
import { colors } from './palettes/colors';
import { createDarkPalette } from './palettes/dark';
import { createLightPalette } from './palettes/light';
import { ThemePreset, portalPreset } from './preset';

export { ColorMode } from '../providers/ColorModeContext';

declare module '@mui/material/styles' {
  interface TypeBackground {
    defaultChannel?: string;
  }

  interface Palette {
    greys: Palette['grey'];
    blues: Palette['grey'];
    greens: Palette['grey'];
    oranges: Palette['grey'];
    reds: Palette['grey'];
    purple: Palette['grey']['500'];
    violet: Palette['grey']['500'];
    // Used by Button/IconButton variants for use in header and sidebar.
    sidebar?: Palette['primary'];
    header?: Palette['primary'];
    tertiary?: Palette['primary'];
  }

  interface PaletteOptions {
    greys?: PaletteOptions['grey'];
    blues?: PaletteOptions['grey'];
    greens?: PaletteOptions['grey'];
    oranges?: PaletteOptions['grey'];
    reds?: PaletteOptions['grey'];
    purple?: Palette['grey']['500'];
    violet?: Palette['grey']['500'];
    // Used by Button/IconButton variants for use in header and sidebar.
    sidebar?: PaletteOptions['primary'];
    header?: PaletteOptions['primary'];
    tertiary?: Palette['primary'];
  }
}
declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides {
    header: true;
    sidebar: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    sidebar: true;
    header: true;
    tertiary: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    sidebar: true;
    header: true;
  }
  interface ButtonPropsVariantOverrides {
    icon: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    code: true;
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    code: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    code?: React.CSSProperties;
  }
}

declare module '@mui/material/Card' {
  interface CardPropsColorOverrides {
    metrics: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    tag: true;
    badge: true;
    filter: true;
  }
}

declare module '@mui/material/Popover' {
  interface PopoverPropsColorOverrides {
    contrast: true;
  }
}

declare module '@mui/material' {
  interface InputBasePropsSizeOverrides {
    large: true;
  }
}

const augmentPalette = (palette: Palette) => ({
  ...palette,
  sidebar: {
    main: '#FFFFFF',
  },
  header: {
    main: '#FFFFFF',
  },
  divider: palette.greys[50],
});

export const createAppTheme = (
  preset: ThemePreset = portalPreset,
  { isSdk = false }: { isSdk?: boolean } = {}
) => {
  const lightPalette = augmentPalette(createLightPalette());
  const darkPalette = augmentPalette(createDarkPalette());
  const spacing = createSpacing(preset.spacingScale);
  const getSpacing = createTheme({ spacing }).spacing;

  return createTheme({
    cssVariables: { colorSchemeSelector: 'data' },
    colorSchemes: {
      light: {
        palette: lightPalette,
      },
      dark: {
        palette: darkPalette,
      },
    },
    defaultColorScheme: 'light',
    spacing,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 800,
        lg: 1000,
        xl: 1200,
      },
    },
    components: {
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontSize: '14px',
            fontWeight: 600,
            width: 'var(--avatar-size)',
            height: 'var(--avatar-size)',
          },
          circular: ({ theme }) => ({
            color: theme.vars.palette.primary.contrastText,
            backgroundColor: theme.vars.palette.primary.main,
          }),
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            background: theme.vars.palette.common.white,
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 'var(--border-radius-m)',
            backgroundColor: theme.vars.palette.common.white,
            outline: 'var(--sdk-card-outline, none)',
          }),
        },
        defaultProps: {
          elevation: 0,
        },
        variants: [
          {
            props: { color: 'metrics' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.greys[50],
              color: theme.vars.palette.greys[500],
            }),
          },
        ],
      },
      MuiCardHeader: {
        defaultProps: {
          slotProps: {
            title: { variant: 'body1', sx: { fontWeight: 600 } },
            subheader: { variant: 'caption', sx: { color: 'greys.500' } },
          },
        },
        styleOverrides: {
          root: {
            flex: 'auto',
            minHeight: 'var(--card-header-min-height)',
            padding: 'var(--card-padding) var(--card-padding) 0',
          },
          content: {
            display: 'flex',
            flexDirection: 'column',
            gap: getSpacing(1),
          },
          action: {
            margin: 0,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: getSpacing(2),
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: 'var(--card-padding)',
            ':last-child': {
              paddingBottom: 'var(--card-padding) !important',
            },
          },
        },
      },
      MuiCardActions: {
        styleOverrides: {
          root: {
            padding: '0 var(--card-padding) var(--card-padding)',
            ':empty': {
              display: 'none',
            },
          },
        },
      },
      MuiPagination: {
        styleOverrides: {
          ul: {
            gap: getSpacing(1),
            'li:first-of-type': {
              marginRight: getSpacing(2),
            },
            'li:last-of-type': {
              marginLeft: getSpacing(2),
            },
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 'var(--border-radius-s)',
            margin: 0,
            color: theme.vars.palette.greys[500],
            '&.Mui-selected': {
              backgroundColor: theme.vars.palette.greys[900],
              color: theme.vars.palette.common.white,
              '&:hover': {
                backgroundColor: theme.vars.palette.greys[900],
              },
            },
          }),
        },
      },
      MuiMenu: {
        defaultProps: isSdk
          ? {
              autoFocus: false,
              disableAutoFocus: true,
              disableAutoFocusItem: true,
              disableRestoreFocus: true,
              disableScrollLock: true,
            }
          : undefined,
        styleOverrides: {
          list: {
            borderRadius: 'var(--border-radius)',
            minWidth: '230px',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: 'var(--dialog-padding)',
            paddingTop: 'var(--dialog-padding) !important',
          },
        },
      },
      MuiDialogActions: {
        defaultProps: {
          disableSpacing: true,
        },
        styleOverrides: {
          root: ({ theme }) => ({
            padding: 'var(--dialog-padding)',
            justifyContent: 'flex-start',
            borderTop: `1px solid ${theme.vars.palette.divider}`,
            gap: getSpacing(2),
            '& > :last-child': {
              marginLeft: getSpacing(1),
            },
            alignItems: 'center',
          }),
        },
      },
      MuiCssBaseline: {
        styleOverrides: theme => ({
          body: {
            backgroundColor: `${theme.vars.palette.background.default} !important`,
          },
        }),
      },
      MuiCircularProgress: {
        styleOverrides: {
          circle: ({ theme }) => ({
            backgroundColor: theme.vars.palette.greys[100],
          }),
          root: ({ theme }) => ({
            color: `${theme.vars.palette.primary.main} !important`,
          }),
        },
      },

      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
          disableTouchRipple: true,
        },
      },
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            borderRadius: 'var(--border-radius-m)',
            fontWeight: 600,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
            '&:active': {
              boxShadow: 'none',
            },
            '&:focus': {
              boxShadow: 'none',
            },
            '&:focus-visible': {
              outline: `1px solid ${theme.vars.palette.primary.main}`,
            },
            '&:disabled, &[aria-disabled="true"]': {
              backgroundColor: theme.vars.palette.greys[100],
              color: theme.vars.palette.greys[300],
              cursor: 'not-allowed',
            },
          }),
          sizeLarge: {
            height: 'var(--control-height-lg)',
            minWidth: 'var(--control-height-lg)',
            padding: '0 var(--control-padding-x-lg)',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
          },
          sizeMedium: {
            height: 'var(--control-height-md)',
            minWidth: 'var(--control-height-md)',
            padding: '0 var(--control-padding-x-md)',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
          },
          sizeSmall: {
            height: 'var(--control-height-sm)',
            minWidth: 'var(--control-height-sm)',
            padding: '0 var(--control-padding-x-sm)',
            fontSize: '0.75rem',
            lineHeight: '1rem',
            borderRadius: 'var(--border-radius-s)',
          },
          endIcon: {
            margin: getSpacing(0, 0, 0, 1),
          },
          startIcon: {
            margin: getSpacing(0, 1, 0, 0),
          },
        },
        variants: [
          {
            props: { color: 'header', variant: 'contained' },
            style: ({ theme }) => ({
              height: 'var(--control-height-chrome)',
              backgroundColor: theme.vars.palette.greys[50],
              color: theme.vars.palette.greys[900],
              '&:hover': {
                outline: `1px solid ${theme.vars.palette.primary.main}`,
              },
            }),
          },
          {
            props: { color: 'sidebar', variant: 'contained' },
            style: ({ theme }) => ({
              height: 'var(--control-height-chrome)',
              // this is does not change from light to dark theme
              backgroundColor: colors.grey[900],
              color: '#FFFFFF',
              '&:hover': {
                outline: `1px solid ${theme.vars.palette.primary.main}`,
              },
            }),
          },
          {
            props: props =>
              (props.variant === 'contained' || props.variant === 'icon') &&
              props.color === 'primary',
            style: ({ theme }) => ({
              backgroundColor: `var(--sdk-primary, ${theme.vars.palette.blues[500]})`,
              color: `var(--sdk-primary-contrast, ${theme.vars.palette.common.white})`,
              '&:hover': {
                backgroundColor: `var(--sdk-primary-dark, ${theme.vars.palette.blues[600]})`,
              },
              '&:active': {
                backgroundColor: `var(--sdk-primary-dark, ${theme.vars.palette.blues[700]})`,
              },
            }),
          },
          {
            props: props =>
              (props.variant === 'contained' || props.variant === 'icon') &&
              props.color === 'secondary',
            style: ({ theme }) => ({
              backgroundColor: `var(--sdk-btn-secondary-bg, ${theme.vars.palette.blues[100]})`,
              color: `var(--sdk-primary-dark, ${theme.vars.palette.blues[800]})`,
              '&:hover': {
                backgroundColor: `var(--sdk-btn-secondary-hover, ${theme.vars.palette.blues[200]})`,
              },
              '&:active': {
                backgroundColor: `var(--sdk-btn-secondary-active, ${theme.vars.palette.blues[300]})`,
              },
            }),
          },
          {
            props: props =>
              (props.variant === 'contained' || props.variant === 'icon') &&
              props.color === 'tertiary',
            style: ({ theme }) => ({
              backgroundColor: `var(--sdk-btn-tertiary-bg, ${theme.vars.palette.common.white})`,
              color: `var(--sdk-primary, ${theme.vars.palette.blues[500]})`,
              '&:hover': {
                backgroundColor: `var(--sdk-primary-light, ${theme.vars.palette.blues[50]})`,
              },
              '&:active': {
                backgroundColor: `var(--sdk-primary-light, ${theme.vars.palette.blues[100]})`,
              },
            }),
          },
          {
            props: props =>
              (props.variant === 'contained' || props.variant === 'icon') &&
              props.color === 'error',
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.reds[500],
              color: theme.vars.palette.common.white,
              '&:hover': {
                backgroundColor: theme.vars.palette.reds[600],
              },
              '&:active': {
                backgroundColor: theme.vars.palette.reds[700],
              },
            }),
          },
          {
            props: { variant: 'text' },
            style: ({ theme }) => ({
              height: 'auto',
              minWidth: 'auto',
              padding: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                color: `var(--sdk-primary, ${theme.vars.palette.blues[600]})`,
              },
              '&:active': {
                color: `var(--sdk-primary-dark, ${theme.vars.palette.blues[700]})`,
              },
              '&:disabled, &[aria-disabled="true"]': {
                backgroundColor: 'transparent',
                color: theme.vars.palette.greys[300],
              },
            }),
          },
          {
            props: { variant: 'icon' },
            style: {
              padding: 0,
              aspectRatio: 1,
            },
          },
        ],
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 'var(--border-radius-m)',
            padding: getSpacing(1),
            gap: getSpacing(1),
            backgroundColor: theme.vars.palette.greys[50],
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: getSpacing(2, 3),
            borderRadius: 'var(--border-radius-s) !important',
            border: 'none !important',
            lineHeight: '20px',
            backgroundColor: theme.vars.palette.greys[50],
            color: theme.vars.palette.greys[500],
            '&:hover, &:focus-visible': {
              backgroundColor: theme.vars.palette.greys[100],
            },
            '&.Mui-selected': {
              backgroundColor: theme.vars.palette.greys[900],
              color: theme.vars.palette.common.white,
              '&:hover, &:focus-visible': {
                color: theme.vars.palette.greys[100],
                backgroundColor: theme.vars.palette.greys[700],
              },
            },
            '&:focus-visible': {
              outline: `1px solid ${theme.vars.palette.blues[500]}`,
            },
          }),
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: '100px',
            transition: 'none',
          },
        },
        variants: [
          {
            props: { color: 'header' },
            style: ({ theme }) => ({
              height: 'var(--control-height-chrome)',
              padding: getSpacing(1.5),
              color: theme.vars.palette.greys[500],
              backgroundColor: theme.vars.palette.greys[50],
            }),
          },
          {
            props: { color: 'sidebar' },
            style: {
              height: 'var(--control-height-chrome)',
              padding: getSpacing(1),
              color: '#FFFFFF',
              backgroundColor: colors.grey[900],
            },
          },
        ],
      },
      MuiOutlinedInput: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 'var(--border-radius-m)',
            transition: 'background-color 0.2s ease-in-out',
            backgroundColor: theme.vars.palette.common.white,
            '&:hover:not(.Mui-focused):not(.Mui-disabled)': {
              backgroundColor: theme.vars.palette.blues[50],
            },
            '&.Mui-error': {
              outline: `1px solid ${theme.vars.palette.reds[500]}`,
            },
            '&.Mui-focused': {
              outline: `1px solid var(--sdk-primary-dark, ${theme.vars.palette.blues[500]})`,
            },
          }),
          notchedOutline: {
            display: 'none',
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: {
            paddingTop: '1px',
          },
        },
      },
      MuiFilledInput: {
        defaultProps: {
          size: 'small',
          disableUnderline: true,
        },

        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 'var(--border-radius-m)',
            transition: 'background-color 0.2s ease-in-out',
            backgroundColor: theme.vars.palette.greys[50],
            '&:hover:not(.Mui-focused):not(.Mui-disabled)': {
              backgroundColor: theme.vars.palette.greys[100],
            },
            '&.Mui-error': {
              backgroundColor: theme.vars.palette.greys[50],
              outline: `1px solid var(--sdk-primary, ${theme.vars.palette.reds[500]})`,
            },
            '&.Mui-focused': {
              backgroundColor: theme.vars.palette.greys[50],
              outline: `1px solid var(--sdk-primary, ${theme.vars.palette.blues[500]})`,
            },
            '&.Mui-disabled': {
              backgroundColor: theme.vars.palette.greys[50],
            },
          }),
          multiline: {
            paddingTop: '0',
          },
        },
        // Mirrors the `tag` Chip palette so a colored Select matches its Chip.
        variants: [
          {
            props: { color: 'info' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.blues[100],
              color: theme.vars.palette.blues[800],
              '&:hover:not(.Mui-focused):not(.Mui-disabled)': {
                backgroundColor: theme.vars.palette.blues[200],
              },
              '&.Mui-focused': {
                backgroundColor: theme.vars.palette.blues[100],
              },
              '& .MuiSelect-icon': {
                color: theme.vars.palette.blues[800],
              },
            }),
          },
          {
            props: { color: 'success' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.greens[100],
              color: theme.vars.palette.greens[800],
              '&:hover:not(.Mui-focused):not(.Mui-disabled)': {
                backgroundColor: theme.vars.palette.greens[200],
              },
              '&.Mui-focused': {
                backgroundColor: theme.vars.palette.greens[100],
              },
              '& .MuiSelect-icon': {
                color: theme.vars.palette.greens[800],
              },
            }),
          },
          {
            props: { color: 'warning' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.oranges[100],
              color: theme.vars.palette.oranges[800],
              '&:hover:not(.Mui-focused):not(.Mui-disabled)': {
                backgroundColor: theme.vars.palette.oranges[200],
              },
              '&.Mui-focused': {
                backgroundColor: theme.vars.palette.oranges[100],
              },
              '& .MuiSelect-icon': {
                color: theme.vars.palette.oranges[800],
              },
            }),
          },
          {
            props: { color: 'error' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.reds[100],
              color: theme.vars.palette.reds[800],
              '&:hover:not(.Mui-focused):not(.Mui-disabled)': {
                backgroundColor: theme.vars.palette.reds[200],
              },
              '&.Mui-focused': {
                backgroundColor: theme.vars.palette.reds[100],
              },
              '& .MuiSelect-icon': {
                color: theme.vars.palette.reds[800],
              },
            }),
          },
          {
            props: { color: 'secondary' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.greys[100],
              color: theme.vars.palette.greys[800],
              '&:hover:not(.Mui-focused):not(.Mui-disabled)': {
                backgroundColor: theme.vars.palette.greys[200],
              },
              '&.Mui-focused': {
                backgroundColor: theme.vars.palette.greys[100],
              },
              '& .MuiSelect-icon': {
                color: theme.vars.palette.greys[800],
              },
            }),
          },
        ],
      },
      MuiSelect: {
        defaultProps: {
          size: 'small',
          variant: 'filled',
        },
        styleOverrides: {
          root: {
            padding: getSpacing(0, 2),
          },
          select: {
            paddingRight: important(getSpacing(5)),
            backgroundColor: 'transparent',
          },
          icon: ({ theme }) => ({
            color: theme.vars.palette.greys[500],
          }),
        },
      },
      MuiList: {
        styleOverrides: {
          padding: {
            padding: getSpacing(1),
            paddingRight: `${getSpacing(1)} !important`,
          },
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: '0.75rem',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '1.25rem',
            textTransform: 'uppercase',
            color: theme.vars.palette.greys[500],
            borderBottom: `1px solid ${theme.vars.palette.greys[50]}`,
            padding: getSpacing(1, 2, 0),
          }),
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 'var(--border-radius-s)',
            padding: getSpacing(2),
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            transition: 'background-color 0.2s ease-in-out',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            '&:hover': {
              backgroundColor: theme.vars.palette.greys[50],
            },
            '&[aria-selected=true]': {
              backgroundColor: theme.vars.palette.blues[100],
            },
            '&[aria-selected=true]:hover': {
              backgroundColor: theme.vars.palette.blues[200],
            },
          }),
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: important('auto'),
            marginRight: getSpacing(2),
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
          },
          secondary: {
            fontSize: '0.75rem',
            lineHeight: '1rem',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--dialog-padding)',
            gap: getSpacing(6),
            minHeight: 'var(--dialog-title-min-height)',
            borderWidth: '0 0 1px 0',
            borderStyle: 'solid',
            borderColor: theme.vars.palette.divider,
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 'var(--border-radius-l)',
          },
          paperFullScreen: {
            borderRadius: 0,
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: 'transparent',
            color: theme.vars.palette.greys[400],
            border: '0',
            borderRadius: '0',
            height: 'auto',
            padding: '0',
            margin: 0,
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
          }),
          positionStart: {
            marginRight: getSpacing(2),
          },
          positionEnd: {
            marginLeft: getSpacing(2),
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            padding: '0 var(--input-padding-x)',
            '&.Mui-disabled': {
              '& > *:not(input)': {
                opacity: 0.3,
              },
            },
            '&.MuiInputBase-sizeSmall > .MuiInputBase-input': {
              paddingTop: 'var(--input-padding-y-sm) !important',
              paddingBottom: 'var(--input-padding-y-sm) !important',
            },
          },
          input: ({ theme }) => ({
            padding: 'var(--input-padding-y) 0 !important',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            '::placeholder': {
              color: theme.vars.palette.greys[400],
              opacity: 1,
            },
          }),
        },
        variants: [
          {
            props: { size: 'large' },
            style: {
              height: 'var(--control-height-lg)',
            },
          },
        ],
      },
      MuiTable: {
        styleOverrides: {
          root: {
            display: 'grid',
            overflowX: 'auto',
            gridColumnGap: 'var(--table-column-gap)',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            display: 'contents',
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            display: 'contents',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: ({ theme }) => ({
            display: 'grid',
            gridColumn: '1 / -1',
            gridTemplateColumns: 'subgrid',
            borderBottom: `1px solid ${theme.vars.palette.divider}`,
            paddingLeft: 'var(--table-row-padding-x)',
            paddingRight: 'var(--table-row-padding-x)',
            // Use the row wrapper's selection highlight.
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: 'transparent',
            },
            '&.Mui-selected:focus-visible': {
              outline: 'none',
            },
          }),
          head: ({ theme }) => ({
            backgroundColor: theme.vars.palette.greys[50],
            borderRadius: 'var(--border-radius-s)',
            border: 'none',
            margin: 0,
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-start',
            border: 'none',
            padding: 'var(--table-cell-padding-y) 0',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            '&.MuiTableCell-alignCenter': {
              justifyContent: 'center',
            },
            '&.MuiTableCell-alignRight': {
              justifyContent: 'flex-end',
            },
          },
          head: ({ theme }) => ({
            fontSize: '0.75rem',
            lineHeight: '1rem',
            color: theme.vars.palette.greys[500],
            padding: 'var(--table-head-cell-padding-y) 0',
            border: 'none',
            svg: {
              marginTop: '-10px',
              marginBottom: '-10px',
            },
          }),
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            svg: {
              height: '0.8rem',
              width: '0.8rem',
              marginLeft: 0,
              marginRight: 0,
            },
            '.MuiTableCell-alignRight &': {
              flexDirection: 'row-reverse',
            },
          },
        },
      },
      MuiChip: {
        defaultProps: {
          variant: 'tag',
        },
        styleOverrides: {
          root: {
            borderRadius: 'var(--border-radius-s)',
            fontSize: '0.75rem',
            lineHeight: '1rem',
          },
          label: {
            padding: 0,
          },
        },
        variants: [
          {
            props: { variant: 'tag' },
            style: {
              height: 'var(--chip-height)',
              padding: getSpacing(0, 2),
              fontWeight: 600,
            },
          },
          {
            props: { variant: 'badge' },
            style: {
              height: 'var(--chip-height-badge)',
              padding: getSpacing(0, 1.5),
              fontWeight: 400,
            },
          },
          {
            props: { variant: 'filter' },
            style: ({ theme }) => ({
              height: 'var(--chip-height)',
              backgroundColor: theme.vars.palette.greys[100],
              color: theme.vars.palette.greys[800],
              padding: getSpacing(0, 2),
              ':hover': {
                backgroundColor: theme.vars.palette.greys[200],
              },
              ':focus': {
                backgroundColor: theme.vars.palette.greys[200],
              },
              '& .MuiChip-deleteIcon': {
                margin: getSpacing(0, 0, 0, 1),
                color: theme.vars.palette.greys[800],
                '&:hover': {
                  color: theme.vars.palette.greys[900],
                },
              },
            }),
          },
          {
            // The selected state of a filter chip.
            props: { variant: 'filter', color: 'primary' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.blues[100],
              color: theme.vars.palette.blues[700],
              ':hover': {
                backgroundColor: theme.vars.palette.blues[200],
              },
              ':focus': {
                backgroundColor: theme.vars.palette.blues[200],
              },
            }),
          },
          {
            props: { variant: 'tag', color: 'info' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.blues[100],
              color: theme.vars.palette.blues[800],
            }),
          },
          {
            props: { variant: 'tag', color: 'primary' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.blues[100],
              color: theme.vars.palette.blues[800],
            }),
          },
          {
            props: { variant: 'tag', color: 'success' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.greens[100],
              color: theme.vars.palette.greens[800],
            }),
          },
          {
            props: { variant: 'tag', color: 'warning' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.oranges[100],
              color: theme.vars.palette.oranges[800],
            }),
          },
          {
            props: { variant: 'tag', color: 'error' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.reds[100],
              color: theme.vars.palette.reds[800],
            }),
          },
          {
            props: { variant: 'tag', color: 'secondary' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.greys[100],
              color: theme.vars.palette.greys[800],
            }),
          },
          {
            props: { variant: 'badge', color: 'info' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.common.white,
              color: theme.vars.palette.greys[600],
            }),
          },
          {
            props: { variant: 'badge', color: 'primary' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.blues[50],
              color: theme.vars.palette.blues[500],
            }),
          },
          {
            props: { variant: 'badge', color: 'success' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.greens[50],
              color: theme.vars.palette.greens[600],
            }),
          },
          {
            props: { variant: 'badge', color: 'warning' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.oranges[50],
              color: theme.vars.palette.oranges[600],
            }),
          },
          {
            props: { variant: 'badge', color: 'error' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.reds[50],
              color: theme.vars.palette.reds[600],
            }),
          },
          {
            props: { variant: 'badge', color: 'secondary' },
            style: ({ theme }) => ({
              backgroundColor: theme.vars.palette.greys[50],
              color: theme.vars.palette.greys[500],
            }),
          },
        ],
      },
      MuiCheckbox: {
        styleOverrides: {
          root: ({ theme }) => ({
            width: '14px',
            height: '14px',
            display: 'grid',
            borderRadius: 'var(--border-radius-xs)',
            outlineWidth: '1px',
            outlineStyle: 'solid',
            outlineColor: theme.vars.palette.greys[200],
            padding: 0,
            margin: '3px',
            transition: 'background-color 0.1s, color 0.1s 0.1s, outline 0.1s',
            color: `var(--sdk-primary, ${theme.vars.palette.blues[500]})`,
            '&:hover': {
              outlineColor: theme.vars.palette.greys[300],
              background: 'transparent',
            },
            '&:focus-within': {
              outlineColor: theme.vars.palette.greys[400],
            },
            '&.Mui-checked': {
              backgroundColor: `var(--sdk-primary, ${theme.vars.palette.blues[500]})`,
              outlineColor: `var(--sdk-primary, ${theme.vars.palette.blues[500]})`,
              color: theme.vars.palette.common.white,
            },
            '&:has(:focus-visible)': {
              outlineColor: `var(--sdk-primary, ${theme.vars.palette.blues[600]})`,
            },
          }),
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: ({ theme }) => ({
            width: '16px',
            height: '16px',
            display: 'grid',
            borderRadius: '16px',
            outlineWidth: '1px',
            outlineStyle: 'solid',
            outlineColor: theme.vars.palette.greys[200],
            padding: 0,
            margin: '2px',
            transition: 'all 0.1s',
            color: theme.vars.palette.common.white,
            flex: 'none',
            '&:hover': {
              outlineColor: theme.vars.palette.greys[300],
              background: 'transparent',
            },
            '&:focus-within': {
              outlineColor: theme.vars.palette.greys[400],
            },
            '&.Mui-checked': {
              color: `var(--sdk-primary, ${theme.vars.palette.blues[500]})`,
              outlineColor: `var(--sdk-primary, ${theme.vars.palette.blues[500]})`,
              outlineOffset: '-4.5px',
              outlineWidth: '5.5px',
            },
            '&:has(:focus-visible)': {
              outlineColor: `var(--sdk-primary, ${theme.vars.palette.blues[600]})`,
            },
          }),
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: getSpacing(1),
            height: getSpacing(1),
            backgroundColor: theme.vars.palette.greys[100],
          }),
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          rounded: {
            borderRadius: 'var(--border-radius-l)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: 'var(--border-radius-l)',
          },
          elevation: {
            boxShadow: '0px 8px 24px 0px #030E121A',
          },
          elevation0: {
            boxShadow: 'none',
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            gap: getSpacing(1),
            margin: '0 !important',
            ':has(.Mui-disabled)': {
              opacity: 0.5,
            },
          },
          label: ({ theme }) => ({
            color: theme.vars.palette.greys[500],
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
          }),
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            display: 'flex',
            gap: getSpacing(1),
            marginTop: getSpacing(2),
            marginLeft: 0,
            marginRight: 0,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: getSpacing(9),
            height: getSpacing(5),
            padding: 0,
          },
          switchBase: ({ theme }) => ({
            padding: 0,
            margin: getSpacing(0.5),
            '&.Mui-checked': {
              transform: 'translateX(16px)',
              '& + .MuiSwitch-track': {
                backgroundColor: `var(--sdk-primary, ${theme.vars.palette.blues[500]})`,
                opacity: 1,
              },
            },
            '&.Mui-disabled': {
              '& + .MuiSwitch-track': {
                opacity: 0.5,
              },
            },
            '&.Mui-focusVisible': {
              '& + .MuiSwitch-track': {
                backgroundColor: `var(--sdk-primary-light, ${theme.vars.palette.greys[400]})`,
              },
            },
            '&:hover + .MuiSwitch-track': {
              backgroundColor: `var(--sdk-switch-hover, ${theme.vars.palette.greys[300]})`,
            },
          }),
          thumb: ({ theme }) => ({
            width: getSpacing(4),
            height: getSpacing(4),
            boxShadow: 'none',
            backgroundColor: theme.vars.palette.common.white,
          }),
          track: ({ theme }) => ({
            borderRadius: getSpacing(5),
            backgroundColor: theme.vars.palette.greys[200],
            transition: 'background-color 0.2s',
            opacity: 1,
            '&::before, &::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              width: getSpacing(4),
              height: getSpacing(4),
            },
            '&::before': {
              diplay: 'block',
              width: getSpacing(0.5),
              height: getSpacing(2),
              backgroundColor: theme.vars.palette.common.white,
              left: getSpacing(2),
            },
            '&::after': {
              diplay: 'block',
              width: getSpacing(2),
              height: getSpacing(2),
              borderRadius: '50%',
              border: `${getSpacing(0.5)} solid ${theme.vars.palette.common.white}`,
              right: getSpacing(1.5),
            },
          }),
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--border-radius-m)',
            padding: getSpacing(4, 6),
            '@media (min-width: 37.5rem)': {
              minWidth: '400px',
            },
            '@media (min-width: 56.25rem)': {
              minWidth: '588px',
            },
          },
          icon: {
            padding: 0,
            display: 'grid',
            placeItems: 'center',
            paddingRight: getSpacing(4),
            marginRight: getSpacing(4),
          },
          action: {
            padding: 0,
            marginLeft: 'auto',
            marginTop: '-1rem',
            marginBottom: '-1rem',
            display: 'grid',
            placeItems: 'center',
          },
          message: {
            padding: 0,
            marginRight: getSpacing(4),
          },
        },
        variants: [
          {
            props: { variant: 'filled', severity: 'success' },
            style: ({ theme }) => ({
              background: theme.vars.palette.greens[200],
              color: theme.vars.palette.greens[900],
              '& .MuiAlert-icon': {
                borderRight: `1px solid ${theme.vars.palette.greens[300]}`,
              },
            }),
          },
          {
            props: { variant: 'filled', severity: 'error' },
            style: ({ theme }) => ({
              background: theme.vars.palette.reds[200],
              color: theme.vars.palette.reds[900],
              '& .MuiAlert-icon': {
                borderRight: `1px solid ${theme.vars.palette.reds[300]}`,
              },
            }),
          },
          {
            props: { variant: 'filled', severity: 'warning' },
            style: ({ theme }) => ({
              background: theme.vars.palette.oranges[200],
              color: theme.vars.palette.oranges[900],
              '& .MuiAlert-icon': {
                borderRight: `1px solid ${theme.vars.palette.oranges[300]}`,
              },
            }),
          },
          {
            props: { variant: 'filled', severity: 'info' },
            style: ({ theme }) => ({
              background: theme.vars.palette.common.white,
              color: theme.vars.palette.greys[900],
              '& .MuiAlert-icon': {
                borderRight: `1px solid ${theme.vars.palette.greys[100]}`,
              },
            }),
          },
        ],
      },
      MuiPopover: {
        defaultProps: isSdk
          ? {
              disableAutoFocus: true,
              disableRestoreFocus: true,
              disableScrollLock: true,
            }
          : undefined,
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundColor: theme.vars.palette.common.white,
            color: theme.vars.palette.greys[900],
          }),
        },
        variants: [
          {
            props: { color: 'contrast' },
            style: ({ theme }) => ({
              '& > .MuiPopover-paper': {
                backgroundColor: theme.vars.palette.greys[900],
                color: theme.vars.palette.common.white,
              },
            }),
          },
        ],
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: ({ theme }) => ({
            backgroundColor: theme.vars.palette.greys[900],
            color: theme.vars.palette.common.white,
            borderRadius: 'var(--border-radius-m)',
            padding: getSpacing(2, 3),
            maxWidth: '20rem',
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
          }),
          arrow: ({ theme }) => ({
            color: theme.vars.palette.greys[900],
          }),
        },
      },
      MuiLink: {
        styleOverrides: {
          root: ({ theme }) => ({
            textDecorationColor: `var(--sdk-primary-light, ${theme.vars.palette.blues[300]})`,
          }),
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&.MuiTypography-code': {
              color: theme.vars.palette.greys[900],
            },
          }),
          h1: ({ theme }) => ({ color: theme.vars.palette.greys[900] }),
          h2: ({ theme }) => ({ color: theme.vars.palette.greys[900] }),
          h3: ({ theme }) => ({ color: theme.vars.palette.greys[900] }),
          h4: ({ theme }) => ({ color: theme.vars.palette.greys[900] }),
          h5: ({ theme }) => ({ color: theme.vars.palette.greys[900] }),
          h6: ({ theme }) => ({ color: theme.vars.palette.greys[900] }),
        },
      },
    },
    typography: {
      fontFamily: preset.fontFamily,
      fontSize: 14,
      h1: { fontFamily: preset.headingFontFamily, ...preset.headings.h1 },
      h2: { fontFamily: preset.headingFontFamily, ...preset.headings.h2 },
      h3: { fontFamily: preset.headingFontFamily, ...preset.headings.h3 },
      h4: { fontFamily: preset.headingFontFamily, ...preset.headings.h4 },
      h5: { fontFamily: preset.headingFontFamily, ...preset.headings.h5 },
      h6: { fontFamily: preset.headingFontFamily, ...preset.headings.h6 },
      body1: {
        fontSize: '1rem',
        lineHeight: '1.5rem',
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
      },
      caption: {
        fontSize: '0.75rem',
        lineHeight: '1rem',
      },
      button: {
        textTransform: 'none',
      },
      code: {
        fontFamily: 'monospace',
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
      },
    },
  });
};
